import { useCallback, useEffect, useId } from "react";

const players = new Map();
const embedRootClaimHandlers = new WeakMap();
/** Maps a native <video>/<audio> element to the player id that claimed it. */
const nativeElementOwners = new WeakMap();
let activeId = null;
let globalGuardInstalled = false;
let hostedEmbedInteractionGuardInstalled = false;

/** Register a claim handler for iframe embed roots (e.g. Facebook). */
export function registerHostedEmbedRootClaim(root, claim) {
  if (root && typeof claim === "function") {
    embedRootClaimHandlers.set(root, claim);
  }
}

export function unregisterHostedEmbedRootClaim(root) {
  if (root) {
    embedRootClaimHandlers.delete(root);
  }
}

/** Pause every native <video>/<audio> except the one that should keep playing. */
export function pauseAllNativeMediaExcept(exceptElement = null) {
  if (typeof document === "undefined") return;
  document.querySelectorAll("video, audio").forEach((el) => {
    if (el !== exceptElement && !el.paused) {
      el.pause();
    }
  });
}

/**
 * Stop iframe audio immediately without removing nodes React still owns.
 * React unmount (via state) handles DOM removal on the next commit.
 */
export function pauseHostedEmbedIframes(container) {
  if (!container || typeof container.querySelectorAll !== "function") return;
  container.querySelectorAll("iframe").forEach((iframe) => {
    try {
      iframe.src = "about:blank";
    } catch {
      // ignore cross-origin restrictions
    }
  });
}

/** @deprecated Use pauseHostedEmbedIframes — kept for any stale imports */
export const teardownHostedEmbedIframes = pauseHostedEmbedIframes;

/** Stop every hosted iframe player except the embed root that is claiming active. */
export function pauseAllHostedEmbedsExcept(exceptRoot = null) {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll(".hosted-video-embed__player-root")
    .forEach((root) => {
      if (exceptRoot && root === exceptRoot) return;
      pauseHostedEmbedIframes(root);
    });
}

function pauseOthers(
  exceptId,
  exceptNativeElement = null,
  exceptEmbedRoot = null,
) {
  players.forEach((pause, id) => {
    if (id !== exceptId) {
      pause();
    }
  });
  pauseAllNativeMediaExcept(exceptNativeElement);
  pauseAllHostedEmbedsExcept(exceptEmbedRoot);
}

/**
 * When any native media starts (including players that forgot to call claimActive),
 * pause other native elements and all embedded players except the current active id.
 */
function installGlobalMediaPlayGuard() {
  if (globalGuardInstalled || typeof document === "undefined") return;
  globalGuardInstalled = true;

  document.addEventListener(
    "play",
    (e) => {
      const el = e.target;
      if (!el || (el.tagName !== "VIDEO" && el.tagName !== "AUDIO")) return;

      const ownerId = nativeElementOwners.get(el);
      if (ownerId) {
        activeId = ownerId;
      }

      pauseAllNativeMediaExcept(el);
      pauseAllHostedEmbedsExcept(null);

      // Stop other registered players (embeds, etc.) but never the native owner.
      const keepPlayingId = ownerId ?? activeId;
      players.forEach((pause, id) => {
        if (keepPlayingId && id === keepPlayingId) return;
        pause();
      });
    },
    true,
  );
}

installGlobalMediaPlayGuard();

/**
 * Clicks inside cross-origin iframes (Facebook) do not bubble to React handlers.
 * focusin / mousedown on the iframe element itself still fires in the parent document.
 */
function installHostedEmbedInteractionGuard() {
  if (hostedEmbedInteractionGuardInstalled || typeof document === "undefined") {
    return;
  }
  hostedEmbedInteractionGuardInstalled = true;

  const handleHostedEmbedInteraction = (event) => {
    const target = event.target;
    if (!target || target.tagName !== "IFRAME") return;
    if (!target.classList.contains("hosted-video-embed__fb-iframe")) return;

    const root = target.closest(".hosted-video-embed__player-root");
    if (!root) return;

    embedRootClaimHandlers.get(root)?.();
  };

  document.addEventListener("focusin", handleHostedEmbedInteraction, true);
  document.addEventListener("mousedown", handleHostedEmbedInteraction, true);
  document.addEventListener("pointerdown", handleHostedEmbedInteraction, true);
}

installHostedEmbedInteractionGuard();

/**
 * Ensures only one hosted/uploaded player is active at a time across the whole page
 * (multiple posts, chat + tribe feed, etc.).
 */
export function useSingleActiveMedia() {
  const id = useId();

  const register = useCallback(
    (pause) => {
      players.set(id, pause);
      return () => {
        players.delete(id);
        if (activeId === id) {
          activeId = null;
        }
      };
    },
    [id],
  );

  /**
   * @param {HTMLMediaElement | null} [nativeElement] keep this native element playing
   * @param {HTMLElement | null} [embedRoot] hosted iframe root to keep mounted
   */
  const claimActive = useCallback(
    (nativeElement = null, embedRoot = null) => {
      activeId = id;
      if (nativeElement) {
        nativeElementOwners.set(nativeElement, id);
      }
      pauseOthers(id, nativeElement ?? null, embedRoot ?? null);
    },
    [id],
  );

  const releaseActive = useCallback(() => {
    if (activeId === id) {
      activeId = null;
    }
  }, [id]);

  return { register, claimActive, releaseActive };
}

/**
 * Registers pause/stop handlers for a media instance.
 */
export function useRegisterSingleActiveMedia(pause, register) {
  useEffect(() => {
    if (typeof pause !== "function") return undefined;
    return register(pause);
  }, [pause, register]);
}
